import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import OpenInFullRoundedIcon from '@mui/icons-material/OpenInFullRounded';
import CloseFullscreenRoundedIcon from '@mui/icons-material/CloseFullscreenRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import TextField from '@mui/material/TextField';
import { COLOR } from '@src/styles/color';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import useExportHistoryEnabled from '@src/hooks/useExportHistoryEnabled';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import actions from '@src/redux/actions';
import {
  CONVERTING_STATUS,
  EXTRACTION_STATUS,
  NOTE_MAX_LENGTH,
  PROJECT_PRODUCT,
} from '@src/constants/project';
import {
  isNoteChanged,
  hasNarrationConfig,
  normalizeNote,
  getExportingState,
} from '@src/services/project';
import SlideNoteTip from './SlideNoteTip';
import {
  TextLinesSkeleton,
  SlideImageFill,
  SlidePreviewImageSkeleton,
} from './ExtractSkeleton';

const StyledSlidePreviewPanel = styled('div')`
  background-color: ${COLOR.white};
  padding: 12px 16px 24px;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  justify-content: center;
  align-items: center;
`;

const StyledScrollArea = styled('div')`
  flex: 1;
  min-height: 0;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  justify-content: safe center;
  align-items: center;
  flex-direction: column;
  gap: 12px;
  ${({ isExpanded }) =>
    isExpanded
      ? `
    align-items: center;
    justify-content: center;
    overflow: hidden;
  `
      : `
    overflow-y: auto;
    overflow-x: hidden;
  `}
`;

const StyledFunctionBar = styled('div')`
  display: flex;
  align-items: center;
  border: 0.5px solid ${COLOR.indigo[16]};
  border-radius: 12px;
  padding: 4px;
  flex-shrink: 0;
  width: fit-content;
  align-self: center;
`;

const StyledToggleButton = styled('button')`
  display: flex;
  align-items: center;
  gap: 4px;
  height: 30px;
  box-sizing: border-box;
  padding: 8px 12px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  background-color: ${({ isActive }) =>
    isActive ? COLOR.indigo[4] : 'transparent'};
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: ${COLOR.label2[100]};
  flex-shrink: 0;

  .toggle-icon {
    width: 20px;
    height: 20px;
    font-size: 20px;
  }
`;

const StyledSlideContent = styled('div')`
  flex: 0 1 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
`;

const StyledSlideImageContainer = styled('div')`
  border-radius: 12px;
  border: 0.25px solid ${COLOR.indigo[16]};
  overflow: hidden;
  width: 100%;
  aspect-ratio: 367 / 207;
  flex: 0 1 auto;
  box-sizing: border-box;

  .slide-image {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .slide-image-placeholder {
    width: 100%;
    height: 100%;
    background-color: ${COLOR.backgroundDefault};
  }
`;

const StyledExpandedImageWrapper = styled('div')`
  min-height: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledExpandedImage = styled('img')`
  display: block;
  max-width: 100%;
  max-height: 100%;
  border-radius: 12px;
  border: 0.25px solid ${COLOR.indigo[16]};
  box-sizing: border-box;
`;

const StyledExpandedPlaceholder = styled('div')`
  width: 100%;
  height: 100%;
  border-radius: 12px;
  border: 0.25px solid ${COLOR.indigo[16]};
  background-color: ${COLOR.backgroundDefault};
`;

const StyledSlideNotes = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  font-size: 14px;
  letter-spacing: 0;

  .slide-notes-label {
    font-weight: 500;
    line-height: 20px;
    color: ${COLOR.darkGray};
  }

  .slide-notes-text {
    font-weight: 400;
    line-height: 20px;
    color: ${COLOR.label2[64]};
    white-space: pre-wrap;
  }

  .slide-notes-counter {
    align-self: stretch;
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    color: ${COLOR.label2[48]};
    text-align: right;
    white-space: nowrap;
  }

  .slide-notes-counter span {
    display: block;
    padding-right: 8px;
  }
`;

const StyledNoteTextBox = styled('div')`
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  padding: 12px 4px 12px 12px;
  overflow: hidden;
  border: 0.5px solid ${COLOR.indigo[32]};
  border-radius: 12px;
`;

const StyledNoteTextField = styled(TextField)`
  width: 100%;

  .MuiInputBase-root {
    width: 100%;
    padding: 0;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: ${COLOR.label2[100]};
  }

  .MuiInputBase-input {
    box-sizing: border-box;
    min-width: 100%;
    padding: 0;
    padding-right: 4px;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: ${COLOR.label2[100]};
  }

  .MuiInputBase-input::placeholder {
    color: ${COLOR.label2[48]};
    opacity: 1;
  }
`;

const StyledNoteWarning = styled('div')`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-top: 8px;
  padding: 12px;
  border-radius: 8px;
  background-color: rgba(255, 149, 0, 0.08);
  box-sizing: border-box;

  .note-warning-icon {
    width: 20px;
    height: 20px;
    color: ${COLOR.textWarning};
    flex-shrink: 0;
  }

  .note-warning-text {
    font-size: 14px;
    line-height: 20px;
    color: ${COLOR.descriptionGray};
    letter-spacing: -0.4px;
    min-width: 0;
    word-break: normal;
    overflow-wrap: normal;
  }

  .note-warning-link {
    display: inline;
    appearance: none;
    border: none;
    background: none;
    padding: 0;
    margin: 0;
    cursor: pointer;
    font: inherit;
    line-height: inherit;
    font-weight: 400;
    color: ${COLOR.blueV2};
    text-decoration: underline;
    vertical-align: baseline;
    white-space: normal;
    word-break: normal;
    overflow-wrap: normal;
  }
`;

const SlidePreviewPanel = ({
  block,
  isExpanded,
  onToggleExpand,
  onOpenAIGenerateDialog,
}) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const source = useSelector((state) => state.project.source);
  const { getFeatureValue } = useFeatureFlags();

  const slideV2Enabled = getFeatureValue(FEATURE_KEYS.PRESENTATION_SLIDE_V2, {
    userId: user?.id,
    email: user?.email,
    phoneNumber: user?.phoneNumber,
  });

  const extractProgressEnabled = getFeatureValue(
    FEATURE_KEYS.PRESENTATION_EXTRACT_PROGRESS,
    { userId: user?.id, email: user?.email, phoneNumber: user?.phoneNumber },
  );
  const isSkeletonUI =
    extractProgressEnabled &&
    source?.extraction?.response?.status === EXTRACTION_STATUS.PENDING;
  // Notes are only skeletoned while extracting when this block has no note AND
  // no block has a note yet (i.e. notes have not been extracted at all). If some
  // blocks already have notes, an empty note is genuine, not still-loading.
  const allNotesEmpty = useSelector((state) =>
    Object.values(state.project.multiBlock?.blocks || {}).every(
      (b) => !b?.originalNote,
    ),
  );
  const isGeneratingNarration = useSelector(
    (state) => state.project.isGeneratingNarration,
  );
  const generatingBlockIds = useSelector(
    (state) => state.project.generatingBlockIds,
  );
  const exports = useSelector((state) => state.project.exports);
  const isDownloading = useSelector((state) => state.project.isDownloading);
  // Converting this slide's text to audio (per-block synthesis) or the whole
  // project — the note must not be editable and AI narration must be blocked.
  const isConverting = block?.status === CONVERTING_STATUS.IN_PROGRESS;
  // While the project is exporting (slide/video/audio) it cannot be edited.
  const exportHistoryEnabled = useExportHistoryEnabled();
  const exportBannerEnabled = getFeatureValue(
    FEATURE_KEYS.PRESENTATION_EXPORT_VIDEO,
    { userId: user.id, email: user.email, phoneNumber: user.phoneNumber },
  );
  const { isExporting } = getExportingState({
    exports,
    isDownloading,
    product: PROJECT_PRODUCT.PRESENTATION,
  });
  // Exporting no longer locks editing (see Presentation/Content), behind the
  // export-history flag.
  const isExportLocked =
    exportBannerEnabled && isExporting && !exportHistoryEnabled;

  const previewImageUrl = block?.previewImageUrl;
  const currentNote = block?.currentNote ?? '';
  const originalNote = block?.originalNote || '';
  const showImageSkeleton = isSkeletonUI && !previewImageUrl;
  // While AI narration is being (re)generated, the target blocks show a note
  // skeleton until polling refreshes them.
  const isGeneratingThisBlock =
    isGeneratingNarration && generatingBlockIds?.includes(block?.id);
  const showNotesSkeleton = isSkeletonUI && !originalNote && allNotesEmpty;
  const [noteValue, setNoteValue] = useState(currentNote);

  useEffect(() => {
    setNoteValue(currentNote);
  }, [currentNote]);

  const handleNoteChange = (e) => {
    const { value } = e.target;
    setNoteValue(value);
    // Pass the existing blockStatus so editing a note does NOT flip a
    // synthesized block to draft (the note is unrelated to synthesis status).
    dispatch(
      actions.project.updateBlock(block.id, {
        currentNote: value,
        blockStatus: block.blockStatus,
      }),
    );
  };

  const handleUseAINarration = () => {
    if (isExportLocked) return;
    onOpenAIGenerateDialog?.(block?.id);
  };

  const noteCharacterCount = normalizeNote(noteValue).length;

  const renderExpandedImage = () => {
    if (previewImageUrl) {
      return <StyledExpandedImage src={previewImageUrl} alt="Slide preview" />;
    }
    if (showImageSkeleton) return <SlidePreviewImageSkeleton />;
    return <StyledExpandedPlaceholder />;
  };

  const renderSlideImage = () => {
    if (previewImageUrl) {
      return (
        <img
          className="slide-image"
          src={previewImageUrl}
          alt="Slide preview"
        />
      );
    }
    if (showImageSkeleton) return <SlideImageFill />;
    return <div className="slide-image-placeholder" />;
  };

  return (
    <StyledSlidePreviewPanel>
      <StyledScrollArea isExpanded={isExpanded}>
        <StyledFunctionBar>
          <StyledToggleButton
            isActive={!isExpanded}
            onClick={onToggleExpand}
            type="button"
          >
            {isExpanded ? (
              <CloseFullscreenRoundedIcon className="toggle-icon" />
            ) : (
              <OpenInFullRoundedIcon className="toggle-icon" />
            )}
            {isExpanded ? t('collapse') : t('expand')}
          </StyledToggleButton>
        </StyledFunctionBar>

        {isExpanded ? (
          <StyledExpandedImageWrapper>
            {renderExpandedImage()}
          </StyledExpandedImageWrapper>
        ) : (
          <StyledSlideContent>
            <StyledSlideImageContainer>
              {renderSlideImage()}
            </StyledSlideImageContainer>

            {slideV2Enabled && !showNotesSkeleton && <SlideNoteTip />}

            <StyledSlideNotes>
              {!showNotesSkeleton && (
                <span className="slide-notes-label">{t('slideNotes')}</span>
              )}

              {showNotesSkeleton && <TextLinesSkeleton />}

              {!showNotesSkeleton &&
                (slideV2Enabled ? (
                  <StyledNoteTextBox>
                    <StyledNoteTextField
                      variant="standard"
                      multiline
                      minRows={3}
                      maxRows={8}
                      fullWidth
                      value={noteValue}
                      onChange={handleNoteChange}
                      placeholder={t('noSlideNotes')}
                      InputProps={{
                        disableUnderline: true,
                        disabled:
                          isSkeletonUI ||
                          isGeneratingThisBlock ||
                          isConverting ||
                          isExportLocked,
                        inputProps: { maxLength: NOTE_MAX_LENGTH },
                      }}
                    />
                    <span className="slide-notes-counter">
                      <span>{`${noteCharacterCount.toLocaleString(
                        language,
                      )}/${NOTE_MAX_LENGTH.toLocaleString(language)}`}</span>
                    </span>
                  </StyledNoteTextBox>
                ) : (
                  <span className="slide-notes-text">
                    {currentNote || t('noSlideNotes')}
                  </span>
                ))}
              {!showNotesSkeleton &&
                slideV2Enabled &&
                hasNarrationConfig(source) &&
                isNoteChanged(block) &&
                !isGeneratingThisBlock &&
                !isConverting && (
                  <StyledNoteWarning>
                    <WarningRoundedIcon className="note-warning-icon" />
                    <span className="note-warning-text">
                      {t('slideNoteChangedBanner')}{' '}
                      <span
                        className="note-warning-link"
                        onClick={handleUseAINarration}
                        role="button"
                        tabIndex={0}
                      >
                        {t('useAINarrationLink')}
                      </span>
                    </span>
                  </StyledNoteWarning>
                )}
            </StyledSlideNotes>
          </StyledSlideContent>
        )}
      </StyledScrollArea>
    </StyledSlidePreviewPanel>
  );
};

export default memo(SlidePreviewPanel);
